import { useCallback, useRef, type ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Box, Stack, Text } from "@similarweb/ui-components/foundations";

import { StyledParenthesisedContent } from "app/pages/sales-intelligence/common-components/styles";
import { useFiltersManager } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersManager";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { LeadsSearchFilterInstance } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilterInstance";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";

import { CrmAppliedIndicator } from "./CrmAppliedIndicator";
import { useScrollToNewlyAddedFilter } from "./hooks/useScrollToNewlyAddedFilter";
import { SelectedLeadsFilterCriteriaSections } from "./SelectedLeadsFilterCriteriaSections";

type CompaniesCountRenderParams = {
    panelValue: Partial<LeadsSearchAllFiltersTypes>;
    isEnabled: boolean;
    dataAutomationPrefix: string;
};

type SelectedFiltersSidebarTranslationKeys = {
    label: string;
    empty: string;
};

const defaultSelectedFiltersSidebarTranslationKeys: SelectedFiltersSidebarTranslationKeys = {
    label: leadsSearchFiltersI18nKeys.selectedFiltersLabel,
    empty: leadsSearchFiltersI18nKeys.selectedFiltersEmpty,
};

type Props = {
    panelValue: Partial<LeadsSearchAllFiltersTypes>;
    onPanelValueUpdate: (next: Partial<LeadsSearchAllFiltersTypes>) => void;
    filtersList: readonly LeadsSearchFilterInstance[];
    dataAutomationPrefix?: string;
    translationKeys?: SelectedFiltersSidebarTranslationKeys;
    renderCompaniesCount?: (params: CompaniesCountRenderParams) => ReactNode;
};

const DEFAULT_DATA_AUTOMATION_PREFIX = "scoring-rule-wizard";

/**
 * Right-hand "Selected" panel for leads filter pickers. Renders each active filter
 * with the same `filterResultView` chips used on scoring rule cards.
 */
export const SelectedFiltersSidebar = ({
    panelValue,
    onPanelValueUpdate,
    filtersList,
    dataAutomationPrefix = DEFAULT_DATA_AUTOMATION_PREFIX,
    translationKeys = defaultSelectedFiltersSidebarTranslationKeys,
    renderCompaniesCount,
}: Props) => {
    const { translate } = useTranslate();

    const typedFiltersList = filtersList as unknown as readonly IFilterInstance<LeadsSearchAllFiltersTypes>[];

    const filtersManager = useFiltersManager<LeadsSearchAllFiltersTypes>({
        filtersValue: panelValue,
        filtersConfig: typedFiltersList,
        onFiltersUpdate: onPanelValueUpdate,
    });

    const dirtyFilterKeys = filtersManager.getDirtyFilters();
    const selectedCount = dirtyFilterKeys.length;
    const isCompaniesCountEnabled = selectedCount > 0;

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const sectionElementsRef = useRef(new Map<string, HTMLElement>());

    const emptyMessage = translate(translationKeys.empty);
    const sidebarAutomation = `${dataAutomationPrefix}.selected-sidebar`;
    const sidebarScrollAutomation = `${dataAutomationPrefix}.selected-sidebar.scroll`;

    const getSectionElement = useCallback((key: string) => sectionElementsRef.current.get(key) ?? null, []);

    const registerSectionRef = useCallback(
        (key: string) => (element: HTMLElement | null) => {
            if (element) {
                sectionElementsRef.current.set(key, element);
            } else {
                sectionElementsRef.current.delete(key);
            }
        },
        [],
    );

    useScrollToNewlyAddedFilter({
        scrollContainerRef,
        dirtyFilterKeys,
        getSectionElement,
    });

    return (
        <Stack
            gap="8px"
            padding="16px"
            borderRadius="10px"
            border={`1px solid ${colorsPalettes.midnight["50"]}`}
            background={colorsPalettes.carbon["0"]}
            width="320px"
            flexShrink={0}
            alignSelf="stretch"
            minHeight="0"
            overflow="hidden"
            data-automation={sidebarAutomation}
        >
            <Text
                fontVariant="h6"
                color={colorsPalettes.carbon["500"]}
                flexShrink={0}
            >
                {translate(translationKeys.label)}
                <StyledParenthesisedContent color={colorsPalettes.carbon["500"]}>
                    {selectedCount}
                </StyledParenthesisedContent>
            </Text>

            {renderCompaniesCount?.({
                panelValue,
                isEnabled: isCompaniesCountEnabled,
                dataAutomationPrefix,
            })}

            <Box
                ref={scrollContainerRef}
                flex="1"
                minHeight="0"
                overflowY="auto"
                data-automation={sidebarScrollAutomation}
            >
                <Stack gap="8px">
                    <CrmAppliedIndicator
                        panelValue={panelValue}
                        onPanelValueUpdate={onPanelValueUpdate}
                        showClearButton
                        isChipActionEnabled
                    />
                    <SelectedLeadsFilterCriteriaSections
                        filtersManager={filtersManager}
                        filtersList={typedFiltersList}
                        showClearButton
                        isChipActionEnabled
                        emptyMessage={emptyMessage}
                        registerSectionRef={registerSectionRef}
                    />
                </Stack>
            </Box>
        </Stack>
    );
};
